/* global ga: true */

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import ImageComponent from '@fuww/components/src/Image';

const getErrorHandler = (action) => ({ detail }) => {
  const category = 'progressive-img';
  const value = 1;
  const label = JSON.stringify(detail);

  try {
    ga('send', 'event', category, action, label, value);
    ga('rollup.send', 'event', category, action, label, value);
  } catch {
    // don't throw if ga is missing
  }

  return true;
};

const Image = ({
  placeholder,
  src,
  alt,
  width,
  height,
  maxHeight,
  objectFit,
  className,
  sizes,
  srcSet,
  cacheKey,
  showOnlyOn,
  finalFetchpriority,
  placeholderFetchpriority,
  imageClassName,
  loadStrategy,
  placeholderLoadStrategy = loadStrategy,
  preload,
  preloadMediaQuery,
  ...properties
}) => {
  const reference = useRef(null);

  useEffect(() => {
    const progressiveImg = reference.current;
    const placeholderErrorHandler = getErrorHandler('placeholderError');
    const errorHandler = getErrorHandler('error');

    progressiveImg.addEventListener(
      'placeholderError',
      placeholderErrorHandler,
    );
    progressiveImg.addEventListener('error', errorHandler);

    return () => {
      progressiveImg.removeEventListener(
        'placeholderError',
        placeholderErrorHandler,
      );
      progressiveImg.removeEventListener('error', errorHandler);
    };
  }, [src]);

  const image = (
    <progressive-img
      placeholder={placeholder}
      src={src}
      alt={alt}
      title={alt}
      class={className}
      srcset={srcSet}
      sizes={sizes}
      final-fetchpriority={finalFetchpriority}
      placeholder-fetchpriority={placeholderFetchpriority}
      ref={reference}
      {...loadStrategy === 'on-visible' ? {} : {
        'load-strategy': loadStrategy,
      }}
      {...placeholderLoadStrategy === 'on-visible' ? {} : {
        'placeholder-load-strategy': placeholderLoadStrategy,
      }}
      {...properties}
    />
  );

  return (
    <>
      {preload && (
        <Head>
          <link
            rel="preload"
            href={placeholder}
            as="image"
            fetchpriority={placeholderFetchpriority}
            media={preloadMediaQuery}
          />
          <link
            rel="preload"
            as="image"
            fetchpriority={finalFetchpriority}
            href={src}
            imageSrcSet={srcSet || ''}
            imageSizes={sizes || ''}
            media={preloadMediaQuery}
          />
        </Head>
      )}
      <ImageComponent
        image={image}
        src={src}
        width={width}
        height={height}
        maxHeight={maxHeight}
        objectFit={objectFit}
        alt={alt}
        srcSet={srcSet}
        sizes={sizes}
        showOnlyOn={showOnlyOn}
        className={imageClassName}
      />
    </>
  );
};

Image.propTypes = {
  placeholder: PropTypes.string,
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string,
  alt: PropTypes.string.isRequired,
  sizes: PropTypes.string.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  maxHeight: PropTypes.string,
  objectFit: PropTypes.string,
  className: PropTypes.string,
  cacheKey: PropTypes.string,
  showOnlyOn: PropTypes.string,
  finalFetchpriority: PropTypes.string,
  placeholderFetchpriority: PropTypes.string,
  imageClassName: PropTypes.string,
  placeholderLoadStrategy: PropTypes.string,
  loadStrategy: PropTypes.string,
  preload: PropTypes.bool,
  preloadMediaQuery: PropTypes.string,
};

Image.defaultProps = {
  placeholder: null,
  width: 'auto',
  height: 'auto',
  maxHeight: 'none',
  objectFit: 'contain',
  className: null,
  srcSet: null,
  cacheKey: null,
  showOnlyOn: null,
  finalFetchpriority: 'low',
  placeholderFetchpriority: 'low',
  imageClassName: null,
  placeholderLoadStrategy: undefined,
  loadStrategy: 'on-visible',
  preload: false,
  preloadMediaQuery: null,
};

export default Image;
